<template>
  <div class="survey">
    <h2>{{ $t('surveyGreeting') }}</h2>
    <p>{{ $t('surveyRatingQuestion') }}</p>
    <p v-html="deliverableName" />
    <div class="align-center check-response dont-response" v-show="!isMandatory">
      <input type="checkbox" @change="dontResponseSurvey()" />
      <label class="send-as-anonymous-label">{{ $t('dontWantToAnswer') }}</label>
    </div>
    <div class="rating-wrapper">
      <survey-box-half-star v-if="deliverableIds.length > 5" @rating-change="updateGrade" :disabled="disabled" />
      <survey-box-star v-if="deliverableIds.length <= 5 && disabled === false" @rating-change="updateGrade"
        :disabled="disabled" />
      <span v-if="grade !== null" class="rating-value">{{ grade.toFixed(1) }}</span>
    </div>
    <div>
      <p>
        {{ $t('surveyCommentPrompt') }}
      </p>
    </div>
    <feedback-tags v-if="grade !== null" @feedback-change="updateObservation" :questionText="questionText"
      :predefinedTags="tags" :clearTags="clearTags" @textarea="disableButton = $event" />
    <div class="align-left mt-3">
      <input v-model="sendAsAnonymous" type="checkbox" />
      <label class="send-as-anonymous-label">{{ $t('sendAsAnonymous') }}</label>
    </div>
    <div class="mt-3" v-show="!sendAsAnonymous">
      <label class="align-left comment-label">{{ $t('nameLabel') }}</label>
      <input v-model="clientName" class="input pa-3" />
      <label class="mt-3 align-left comment-label">{{ $t('emailLabel') }}</label>
      <input v-model="clientEmail" class="input pa-3" />
    </div>
    <div class="mt-3 btn-container">
      <button :class="{ 'btn': true, 'btn--disabled': checkStatusButton }" :disabled="checkStatusButton" @click="send">
        {{ $t('sendButton') }}
      </button>
    </div>
  </div>
</template>
<script>
import { Tags } from '../domain/entity/Tags';
import SurveyBoxHalfStar from '../components/SurveyBoxHalfStar.vue';
import SurveyBoxStar from '../components/SurveyBoxStar.vue';
import FeedbackTags from '../components/FeedbackTags.vue';
import Survey from '../domain/entity/Survey.entity';
import SurveyService from '../domain/service/SurveyService';
import { createToast } from 'mosha-vue-toastify';
import 'mosha-vue-toastify/dist/style.css';

export default {
  components: {
    SurveyBoxHalfStar,
    SurveyBoxStar,
    FeedbackTags
  },
  data: () => ({
    deliverableIds: [],
    deliverableName: '',
    grade: null,
    observation: '',
    sendAsAnonymous: true,
    clientName: '',
    clientEmail: '',
    disabled: false,
    disableButton: false,
    isMandatory: true,
    clearTags: false,
  }),
  computed: {
    tags() {
      if (this.grade === null) {
        return [];
      }
      return this.grade === 5 ? Tags.Positive : Tags.Negative;
    },
    questionText() {
      return this.grade === 5 ? this.$t('positiveQuestionText') :
        this.$t('negativeQuestionText');
    },
    checkStatusButton() {
      return (this.grade === null && this.disabled === false) || this.disableButton;
    }
  },
  methods: {
    updateGrade(grade) {
      this.grade = grade;
      this.observation = '';
      this.clearTags = true;
    },
    updateObservation(observation) {
      this.clearTags = false;
      this.observation = observation;
    },
    dontResponseSurvey() {
      this.disabled = !this.disabled;
      this.observation = null;
      this.grade = null;
    },
    getSurvey() {
      if (this.sendAsAnonymous) {
        return new Survey({
          deliverableIds: this.deliverableIds,
          grade: this.grade,
          observation: this.observation,
        });
      }
      return new Survey({
        deliverableIds: this.deliverableIds,
        grade: this.grade,
        observation: this.observation,
        clientName: this.clientName,
        clientEmail: this.clientEmail,
      });
    },
    send() {
      const survey = this.getSurvey();

      if (survey.grade !== null) {
        SurveyService.answerSurvey(survey)
          .then().catch(() => {
            const errorMessage = this.$t('surveySendErrorMessage');
            createToast(errorMessage, { type: 'danger' });
          }
          );
      }

      createToast(this.$t('surveySendSuccessMessage'), { type: "success" });
      this.$router.push({ name: 'thank-you' });
    },
  },
  created() {
    const idSurvey = this.$route.params.id;

    SurveyService.getLinkSurvey(idSurvey)
      .then(result => {
        const data = result.data;

        this.deliverableIds = data.deliverables.map(x => x.id);

        if (data.demandName && data.demandName.trim() !== "") {
          this.deliverableName = data.demandName;
        } else {
          this.deliverableName = data.deliverables.map(x => x.name).join('<br>');
        }

        this.isMandatory = data.isMandatory;
      })
      .catch(() => {
        const errorMessage = this.$t('surveyFetchErrorMessage');
        createToast(errorMessage, { type: 'danger' });
      });
  },
}
</script>
<style scoped>
.survey {
  margin-top: 40px;
  width: 395px;
  margin: 0 auto;
}

.survey__boxes {
  margin-bottom: 20px;
}

.rating-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 10px;
}

.rating-value {
  font-size: 22px;
  font-weight: 600;
  color: #2563eb;
  min-width: 40px;
  text-align: left;
}

.btn-container {
  margin-top: 20px;
}

.btn {
  padding: 8px;
  border: none;
  background-color: #41172b;
  color: white;
  border-radius: 4px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  height: 40px;
  width: 100px;
  cursor: pointer;
}

.btn.btn--disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  box-shadow: none;
}

.input {
  background: #f3f3f3;
  border: 1px solid #bbbbbb;
  box-sizing: border-box;
  border-radius: 8px;
  width: 100%;
  margin-top: 2px;

}

.align-left {
  display: flex;
  justify-content: flex-start;
}

.send-as-anonymous-label {
  font-size: 14px;
  margin-top: 2px;
}

.comment-label {
  font-size: 14px;
  font-weight: bold;
}

.mt-3 {
  margin-top: 12px;
}

.pa-4 {
  padding: 16px;
}

.pa-3 {
  padding: 12px;
}

.disabled {
  opacity: 0.4;
  pointer-events: none;
  padding-bottom: 20px;
}

.dont-response {
  padding-bottom: 20px;
}
</style>
